import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { ForgeAutocomplete } from '@tylertech/forge-react';
import TextFieldWrapper from '../TextFieldWrapper/TextFieldWrapper.js';
import { createRandomID } from '../reusableFunctions.js';

const SELECT_ALL_VALUE = 'Select all';
const SELECT_ALL_OPTION = { label: 'Select all', value: SELECT_ALL_VALUE };

const Autocomplete = ({
  options,
  value,
  onChange,
  label,
  required,
  disabled,
  multiple = false,
  showClear = false,
  invalid = false,
  style,
  classes,
  supportText,
  accLabel,
  sortOptions = true,
  setHasInteracted,
  variant,
  syncPopupWidth = true,
  debounceValue = 500,
  forceCloseOnChange = false,
  fastForwardSelection = false,
}) => {
  const [currentSelection, setCurrentSelection] = useState([]);
  const autocompleteRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const notifyChange = (nextValue) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (debounceValue > 0) {
      debounceTimerRef.current = setTimeout(() => {
        onChange(nextValue);
        debounceTimerRef.current = null;
        if (forceCloseOnChange) {
          autocompleteRef.current.open = false;
        }
      }, debounceValue);
      return;
    }

    onChange(nextValue);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const dialogContent = document.querySelector('.dialog-content');
    if (!dialogContent) return;

    const handleScroll = () => {
      if (autocompleteRef.current.open) {
        autocompleteRef.current.open = false;
      }
    };

    dialogContent.addEventListener('scroll', handleScroll);
    return () => dialogContent.removeEventListener('scroll', handleScroll);
  }, []);

  const normalized = useMemo(() => {
    let list = options?.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)) || [];
    if (sortOptions) {
      list = list.sort((a, b) => a.label.localeCompare(b.label));
    }
    return multiple ? [SELECT_ALL_OPTION, ...list] : list;
  }, [options, sortOptions]);

  const realOptions = useMemo(() => normalized.filter(o => o.value !== SELECT_ALL_VALUE), [normalized]);
  const allRealValues = useMemo(() => realOptions.map(o => o.value), [realOptions]);

  const getSelectionFromValue = (nextValue) => {
    if (multiple) {
      const selectedObjects = realOptions.filter((o) => nextValue.includes(o.value));
      const isFull = selectedObjects.length === realOptions.length && realOptions.length > 0;
      return isFull ? [SELECT_ALL_OPTION, ...selectedObjects] : selectedObjects;
    }

    return realOptions.find((o) => o.value === nextValue) || null;
  };

  useEffect(() => {
    if (multiple) {
      const selectedObjects = realOptions.filter((o) => value.includes(o.value));
      const isFull = selectedObjects.length === realOptions.length && realOptions.length > 0;
      
      if (isFull) {
        setCurrentSelection([SELECT_ALL_OPTION, ...selectedObjects]);
      } else {
        setCurrentSelection(selectedObjects);
      }
    } else {
      const selection = realOptions.find((o) => o.value === value);
      setCurrentSelection(selection || null);
    }
  }, [value, normalized, multiple, realOptions]);

  const handleChange = (e) => {
    if (!multiple) {
      const val = e.detail;
      const nextValue = typeof val === 'object' ? val?.value : val;
      setCurrentSelection(getSelectionFromValue(nextValue));
      notifyChange(nextValue);
      return;
    }

    const incomingValues = e.detail.map(v => typeof v === 'object' ? v.value : v);
    const wasSAInUI = currentSelection.some(o => o.value === SELECT_ALL_VALUE);
    const isSAInEvent = incomingValues.includes(SELECT_ALL_VALUE);
    const currentValues = currentSelection
      .map(o => o.value)
      .filter(v => v !== SELECT_ALL_VALUE);
    const wasFull = currentValues.length === allRealValues.length;
    let nextValue;

    if (isSAInEvent && !wasSAInUI) {
      nextValue = allRealValues;
    } 
    else if (wasSAInUI && !isSAInEvent && wasFull && incomingValues.length >= allRealValues.length) {
      nextValue = [];
    }
    else {
      nextValue = incomingValues.filter(v => v !== SELECT_ALL_VALUE);
    }

    setCurrentSelection(getSelectionFromValue(nextValue));
    notifyChange(nextValue);
  };

  const filter = (text = '') => {
    const q = text?.trim().toLowerCase();
    if (!q) return normalized; 
    const filteredData = normalized.filter(o => 
      o.value !== SELECT_ALL_VALUE && o.label.toLowerCase().includes(q)
    );
    return multiple ? [SELECT_ALL_OPTION, ...filteredData] : filteredData;
  };

  // Input Text Builder: Handle single vs multiple display
  const selectedTextBuilder = (selectedItems) => {
    const realSelected = selectedItems.filter(item => item.value !== SELECT_ALL_VALUE);
    const count = realSelected.length;
    if (count === 0) return '';
    if (count === 1) return realSelected[0].label;
    return `${count} options selected`;
  };

  // DOM Fix: Force Select All(SA) to stay at index 0 against Forge's internal sorting
  useEffect(() => {
    const forceSAToTop = () => {
      const list = document.querySelector('forge-popover forge-list');
      if (list) {
        const items = Array.from(list.querySelectorAll('forge-list-item'));
        const saItem = items.find(item => item.value === SELECT_ALL_VALUE);
        if (saItem && list.firstChild !== saItem) {
          list.prepend(saItem);
        }
      }
    };

    const observer = new MutationObserver(() => forceSAToTop());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // --- TOOLTIP LOGIC ---
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const items = document.querySelectorAll('forge-popover > forge-list > forge-list-item');
      items.forEach(item => {
        const shadowRootContentElement = item.shadowRoot?.querySelector('[part="root"]>[part="text-container"]');
        const slot = shadowRootContentElement?.querySelector('slot');
        const assignedNodes = slot?.assignedNodes();
        let textElement;
        assignedNodes?.forEach(node => { if (node.nodeType === Node.ELEMENT_NODE) textElement = node; });

        if (textElement && textElement.scrollWidth > textElement.clientWidth) {
          const tooltip = document.createElement('forge-tooltip');
          tooltip.setAttribute('anchor', textElement.textContent);
          const parentElement = textElement.parentElement;
          parentElement.setAttribute('id', textElement.textContent);
          tooltip.textContent = textElement.textContent;
          parentElement.appendChild(tooltip);
        } else {
          item.removeAttribute('id');
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <ForgeAutocomplete
      mode={!multiple ? 'stateless' : 'default'}
      ref={autocompleteRef}
      invalid={invalid}
      multiple={multiple}
      disabled={disabled}
      value={currentSelection}
      selectedTextBuilder={selectedTextBuilder}
      filter={filter}
      // hard dependency on state.fireUnsavedChangesDialog
      {...(multiple && {
        'on-forge-autocomplete-change': (e) => handleChange(e),
      })}
      {...(!multiple && {
        'on-forge-autocomplete-select': fastForwardSelection ? () => onChange(currentSelection) : (e) => handleChange(e)
      })}
      {...(!syncPopupWidth && {
        popupClasses: 'shortTermUserGroupDropDownClass',
      })}
      syncPopupWidth={syncPopupWidth}
      syncSelected={false}
      filterOnFocus
      style={style}
      className={classes}
      id={`autocomplete-${label.replaceAll(' ', '-').toLowerCase()}-${createRandomID()}`}
    >
      <TextFieldWrapper
        label={label}
        accLabel={accLabel}
        required={required}
        disabled={disabled}
        invalid={invalid}
        showClear={showClear}
        supportText={supportText}
        popoverIcon
        setHasInteracted={setHasInteracted}
        variant={variant}
      />
    </ForgeAutocomplete>
  );
};

Autocomplete.propTypes = {
  options: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  onChange: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  showClear: PropTypes.bool,
  invalid: PropTypes.bool,
  style: PropTypes.object,
  classes: PropTypes.string,
  supportText: PropTypes.string,
  accLabel: PropTypes.string,
  sortOptions: PropTypes.bool,
  setHasInteracted: PropTypes.func,
  variant: PropTypes.string,
  syncPopupWidth: PropTypes.bool,
  debounceValue: PropTypes.number,
  forceCloseOnChange: PropTypes.bool,
  fastForwardSelection: PropTypes.bool,
};

export default Autocomplete;
